import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';

/**
 * Handle deep links from Stripe and other sources
 * This is crucial for iOS/Android native apps when returning from external URLs
 */
export const useDeepLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // console.log('[DeepLinkHandler] useEffect running');
    
    // Check if running in Capacitor
    const isCapacitor = (window as any).Capacitor?.isNativePlatform?.() || false;
    // console.log('[DeepLinkHandler] Is Capacitor?', isCapacitor);
    
    if (!isCapacitor) {
      // Not in native app, skip deep link handling
      // console.log('[DeepLinkHandler] Not in Capacitor, skipping deep link setup');
      return;
    }
    
    // console.log('[DeepLinkHandler] Setting up deep link listeners');

    // Handle app URL open events (deep links)
    const handleAppUrlOpen = async (data: { url: string }) => {
      // console.log('Deep link received:', data.url);
      
      // Parse the URL
      try {
        // Handle both dinnerbrain:// and com.dinnerbrain.app:// URLs
        let path = '';
        if (data.url.startsWith('dinnerbrain://')) {
          path = data.url.replace('dinnerbrain://', '');
        } else if (data.url.startsWith('com.dinnerbrain.app://')) {
          path = data.url.replace('com.dinnerbrain.app://', '');
        } else {
          // console.log('Unhandled URL scheme:', data.url);
          return;
        }
        
        const [route, queryString] = path.split('?');
          
          // Parse query parameters
          const params = new URLSearchParams(queryString || '');
          
          // console.log('Deep link route:', route);
          // console.log('Query params:', Object.fromEntries(params.entries()));
          
          // Handle different routes (normalize app/ prefix)
          const normalizedRoute = route.startsWith('app/') ? route.substring(4) : route;
          
          if (normalizedRoute === 'signup' || route === 'signup') {
            // Handle Stripe return for signup
            const sessionId = params.get('session_id');
            const email = params.get('email');
            const plan = params.get('plan');
            
            // console.log('Navigating to signup with:', { sessionId, email, plan });
            
            if (sessionId && email && plan) {
              // Navigate to signup page with params
              navigate(`/signup?session_id=${sessionId}&email=${encodeURIComponent(email)}&plan=${plan}`);
            } else if (sessionId) {
              // Minimal params - just session ID
              navigate(`/signup?session_id=${sessionId}`);
            }
          } else if (route === 'app/recipes' || normalizedRoute === 'recipes') {
            // Handle Stripe return for authenticated users
            const sessionId = params.get('session_id');
            
            if (sessionId) {
              navigate(`/app/recipes?session_id=${sessionId}`);
            } else {
              navigate('/app/recipes');
            }
          } else if (route === 'onboarding') {
            // Handle cancel from Stripe
            navigate('/onboarding');
          } else {
            // Default navigation
            navigate(`/${route}`);
          }
      } catch (error) {
        // console.error('Error handling deep link:', error);
      }
    };

    // Register the listener
    // console.log('[DeepLinkHandler] Registering appUrlOpen listener');
    const listener = App.addListener('appUrlOpen', handleAppUrlOpen);
    // console.log('[DeepLinkHandler] Listener registered:', listener);

    // Also check if app was opened with a URL (initial launch)
    // console.log('[DeepLinkHandler] Checking for launch URL');
    App.getLaunchUrl().then((launchUrl) => {
      // console.log('[DeepLinkHandler] Launch URL result:', launchUrl);
      if (launchUrl?.url) {
        console.log('[DeepLinkHandler] Processing launch URL:', launchUrl.url);
        handleAppUrlOpen({ url: launchUrl.url });
      } else {
        console.log('[DeepLinkHandler] No launch URL found');
      }
    }).catch(error => {
      console.log('[DeepLinkHandler] Error getting launch URL:', error);
    });

    // Cleanup
    return () => {
      listener.then(l => l.remove());
    };
  }, [navigate]);
};